import { gql } from '@reverbdotcom/commons/src/gql';
import React from 'react';
import I18n from 'i18n-js';
import { Item2ProductModuleRefreshedDataFragment } from '@reverbdotcom/commons/src/gql/graphql';
import { Item2ReportMismatchedProduct } from './item2_report_mismatched_product';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import Item2ProductModuleListingRow from './item2_product_module_listing_row';
import { getLowestPrice } from './item2_product_module_header';
import BestSellerBadge from './best_seller_badge';
import VideoEmbed from '@reverbdotcom/commons/src/components/video_embed';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import { RCGrid, RCImage, buttonClassName } from '@reverbdotcom/cadence/components';


interface IProps {
  listing: Item2ProductModuleRefreshedDataFragment;
}

const COMPONENT_NAME = 'Item2ProductModuleRefreshed';

export const Item2ProductModuleRefreshedFragment = gql(`
fragment Item2ProductModuleRefreshedData on Listing {
  _id
  id
  canonicalProductId
  condition {
    conditionSlug
  }
  categories {
    _id
    id
    slug
    name
    rootSlug
    webLink {
      href
    }
    cmsLink {
      href
    }
    parentCategories {
      _id
      slug
    }
  }
  csp {
    _id
    id
    title
    slug
    summary
    webLink {
      path
    }
    image(input: {transform: "card_square"}) {
      source
    }
    videos {
      videoLinks {
        youtubeVideoId
      }
    }
    inventory {
      newLowPrice {
        display
        amountCents
      }
      usedLowPrice {
        display
        amountCents
      }
      usedTotal
      newTotal
    }
  }
}
`);

export function componentClickEvent(displayStyle, path) {
  return {
    eventName: MParticleEventName.ClickedComponent,
    componentName: COMPONENT_NAME,
    displayStyle,
    entityType: 'url',
    entityId: path,
  };
}

export function Item2ProductModuleRefreshed(props: IProps) {
  const { csp, categories, canonicalProductId, condition } = props.listing;

  if (!csp) return null;

  const cspVideos = csp.videos?.videoLinks?.[0];
  const desktopSummaryCol = cspVideos ? 6 : 8;
  const rowMinCount = csp.summary || cspVideos ? 0 : 3;
  const lowestPrice = getLowestPrice(csp.inventory);
  const totalInventoryCount = csp.inventory?.usedTotal + csp.inventory?.newTotal;

  return (
    <div  className="mb-20 mobile-mb-8">
      <div className="item2-product-module-refreshed__heading">{I18n.t('discovery.item2ProductModule.productOverview')}</div>
      <RCGrid>
        <RCGrid.Item
          key={0}
          colSpan={{ desktop: 2, tablet: 3, mobile: 4 }}
          rowSpan={{ desktop: 2, mobile: 1 }}
          order={{ desktop: 1, mobile: 2 }}
        >
          {csp.image?.source &&
            <CoreLink
              to={csp.webLink?.path}
              className="item2-product-module-refreshed__image"
              clickEvent={componentClickEvent('imageLink', csp.webLink?.path)}
            >
              <RCImage
                src={csp.image.source}
                overlay="offwhiteWithHover"
                borderRadius="lg"
              />
            </CoreLink>
          }
        </RCGrid.Item>

        <RCGrid.Item
          key={1}
          colSpan={{ desktop: desktopSummaryCol, tablet: 8, mobile: 8 }}
          rowSpan={{ desktop: 1 }}
          order={{ desktop: 2,  mobile: 1 }}
        >
          <div className="d-flex fx-dir-col gap-3 mobile-gap-2">
            <h4>
              <CoreLink
                to={csp.webLink?.path}
                className="item2-product-module-refreshed__title"
                clickEvent={componentClickEvent('titleLink', csp.webLink?.path)}
              >
                {csp.title}
              </CoreLink>
            </h4>
            <div className="item2-product-module__badges-refreshed">
              <BestSellerBadge
                cspId={csp.id}
                categories={categories}
              />
            </div>
          </div>
        </RCGrid.Item>

        <RCGrid.Item
          key={2}
          colSpan={{ desktop: 4, tablet: 12 }}
          rowSpan={{ desktop: 2 }}
          order={{ desktop: 3, tablet: 5 }}
        >
          { !!cspVideos &&
            <div className="item2-product-module-refreshed__csp-video">
              <VideoEmbed slug={cspVideos?.youtubeVideoId} />
            </div>
          }
        </RCGrid.Item>

        <RCGrid.Item
          key={3}
          colSpan={{ desktop: desktopSummaryCol, tablet: 8, mobile: 12 }}
          rowSpan={{ desktop: 1 }}
          order={{ desktop: 4,  mobile: 3 }}
        >
          <div className="item2-product-module-refreshed__summary-content">
            <SanitizedRender
              htmlTag="div"
              className="item2-product-module-refreshed__summary"
              html={csp.summary}
              truncationHeight={72}
              withoutFade
            />
            {totalInventoryCount > 1 &&
              <CoreLink
                to={csp.webLink?.path}
                className={buttonClassName({
                  variant: 'outlined',
                })}
                clickEvent={componentClickEvent('ctaLink', csp.webLink?.path)}
              >
                <div>
                  {I18n.t('discovery.item2ProductModule.lowestFromRefreshed', { count: totalInventoryCount, price: lowestPrice })}
                </div>
                <span className="icon-r-arrow-right" />
              </CoreLink>
            }
          </div>
        </RCGrid.Item>

        <RCGrid.Item
          key={4}
          colSpan={{ desktop: 12 }}
          order={{ desktop: 5, tablet: 4, mobile: 4 }}
        >
          <Item2ProductModuleListingRow
            canonicalProductId={canonicalProductId}
            listingCondition={condition.conditionSlug}
            minCount={rowMinCount}
          />
        </RCGrid.Item>

        <RCGrid.Item
          key={5}
          colSpan={{ desktop: 12 }}
          order={{ desktop: 6 }}
        >
          <div className="item2-product-module-refreshed__mismatch">
            <Item2ReportMismatchedProduct
              listingId={props.listing.id}
              variant="transparent"
            />
          </div>
        </RCGrid.Item>
      </RCGrid>
    </div>
  );
}
